// src/components/SurveillanceHistory.jsx
import React from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { HistoryGrid } from '@1823-partners/core';

dayjs.extend(utc);

const FIELD_NAMES = {
  CUSIP: 'CUSIP',
  SECURITY: 'SECURITY',
  NAIC_DESIGNATIONS: 'NAIC DESIGNATIONS',
  BROAD_ASSET_CLASS: 'BROAD ASSET CLASS',
  FINAL_GROUPING: 'FINAL GROUPING',
  BOOKVALUE: 'BOOKVALUE',
  GAINLOSS: 'GAINLOSS',
  CARRYVALUE: 'CARRYVALUE',
  MARKETPRICE: 'MARKETPRICE',
  BOOKPRICE: 'BOOKPRICE',
  WAL_F: 'WAL F',
  YLD_F: 'YLD F',
  SPREAD: 'SPREAD',
  WARF: 'WARF',
  CE: 'CE',
  THICK: 'THICK',
  CPR1M: 'CPR1M', CPR3M: 'CPR3M', CPR6M: 'CPR6M', CPR12M: 'CPR12M',
  CDR1M: 'CDR1M', CDR3M: 'CDR3M', CDR6M: 'CDR6M', CDR12M: 'CDR12M',
  SEV1M: 'SEV1M', SEV3M: 'SEV3M', SEV6M: 'SEV6M', SEV12M: 'SEV12M',
  DQ30P: 'DQ30P', DQ60P: 'DQ60P',
  LTV: 'LTV', LEVERAGE: 'LEVERAGE', OCCUPANCY: 'OCCUPANCY', WAC: 'WAC',
  RTG_SP: 'RTG SP', RTG_MOODY: 'RTG MOODY', RTG_FITCH: 'RTG FITCH',
  GROUP_W_RATING: 'GROUP W RATING',
  SETTLE_DT: 'SETTLE DT', ASOFDT: 'ASOFDT',
  SPECSERV: 'SPECSERV', DEBTSERV: 'DEBTSERV', NOIDY: 'NOIDY',
  UTILIZATION: 'UTILIZATION', MVOC: 'MVOC', CCC: 'CCC',
  CDO_WARF: 'CDO WARF', SSSGT: 'SSSGT', AAA_SPREAD: 'AAA SPREAD',
  CDO_MP: 'CDO MP', ACCUMLOSS: 'ACCUMLOSS',
  RTG_KBRA: 'RTG KBRA', RTG_DBRS: 'RTG DBRS', RTG_BSCR: 'RTG BSCR', RTG_EJ: 'RTG EJ',
  SETTLE_DATE: 'SETTLE DATE', CALLABLE: 'CALLABLE', INTEXSTATUS: 'INTEXSTATUS', LOADID: 'LOADID',
  COLLAT_MANAGER: 'COLLAT MANAGER', ISSUER_TRUST: 'ISSUER TRUST', NOTES: 'NOTES',
};

// --- Value formatters ---

const fmtCurrencyNoDecimals = (params) => {
  if (params.value == null || params.value === '') return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(params.value);
};

const fmtPrice = (params) => {
  if (params.value == null || params.value === '') return '';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(params.value);
};

const fmtPercent2 = (params) => {
  if (params.value == null || params.value === '') return '';
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(params.value / 100);
};

const fmtSpreadBps = (params) => {
  if (params.value == null || params.value === '' || params.value === 0) return '';
  return `${Number(params.value).toFixed(2)} bps`;
};

const fmtPct1 = (params) => {
  if (params.value == null || params.value === '') return '';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(params.value) + '%';
};

const fmtCCCPct = (params) => {
  if (params.value == null || params.value === '') return '';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(params.value * 100) + '%';
};

const fmtLeverage = (params) => {
  if (params.value == null || params.value === '') return '';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(params.value) + 'x';
};

const fmtIntNoDecimals = (params) => {
  if (params.value == null || params.value === '') return '';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(params.value);
};

const fmtNum1 = (params) => {
  if (params.value == null || params.value === '') return '';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(params.value);
};

const fmtDate = (params) => {
  if (!params.value) return '';
  try {
    // Date-only fields (ASOFDT, SETTLE_DATE): render in the UTC frame so values
    // do not shift a day for clients west of UTC. 'M/D/YYYY' mirrors the prior
    // toLocaleDateString() default.
    return dayjs.utc(params.value).format('M/D/YYYY');
  } catch {
    return params.value;
  }
};

// --- Helper to build a numeric column ---
const numCol = (field, headerName, valueFormatter, extra = {}) => ({
  field,
  headerName,
  type: 'numericColumn',
  valueFormatter,
  ...extra,
});

// --- Helper to build a text column ---
const textCol = (field, headerName, extra = {}) => ({
  field,
  headerName,
  ...extra,
});

// --- Column definitions (business fields only -- no version, MODIFIED_BY, MODIFIED_DATE) ---
const COLUMNS = [
  textCol('CUSIP', 'CUSIP'),
  textCol('SECURITY', 'SECURITY'),
  textCol('NAIC_DESIGNATIONS', 'NAIC DESIGNATIONS'),
  textCol('BROAD_ASSET_CLASS', 'BROAD ASSET CLASS'),
  textCol('FINAL_GROUPING', 'FINAL GROUPING'),

  // Currency (no decimals)
  numCol('BOOKVALUE', 'BOOKVALUE', fmtCurrencyNoDecimals),
  numCol('GAINLOSS', 'GAINLOSS', fmtCurrencyNoDecimals),
  numCol('CARRYVALUE', 'CARRYVALUE', fmtCurrencyNoDecimals),
  numCol('ACCUMLOSS', 'ACCUMLOSS', fmtCurrencyNoDecimals),

  // Prices (2 decimals)
  numCol('MARKETPRICE', 'MARKETPRICE', fmtPrice),
  numCol('BOOKPRICE', 'BOOKPRICE', fmtPrice),
  numCol('CDO_MP', 'CDO MP', fmtPrice),

  // WAL / DEBTSERV (1 decimal, plain number)
  numCol('WAL_F', 'WAL F', fmtNum1),
  numCol('DEBTSERV', 'DEBTSERV', fmtNum1),

  // Yield (percent with 2 decimals, value / 100)
  numCol('YLD_F', 'YLD F', fmtPercent2),

  // Spreads (bps)
  numCol('SPREAD', 'SPREAD', fmtSpreadBps),
  numCol('AAA_SPREAD', 'AAA SPREAD', fmtSpreadBps),

  // WARF (integer)
  numCol('WARF', 'WARF', fmtIntNoDecimals),

  // CDO_WARF (1 decimal)
  numCol('CDO_WARF', 'CDO WARF', fmtNum1),

  // Percentage fields (1 decimal + %)
  numCol('CE', 'CE', fmtPct1),
  numCol('THICK', 'THICK', fmtPct1),
  numCol('CPR1M', 'CPR1M', fmtPct1),
  numCol('CPR3M', 'CPR3M', fmtPct1),
  numCol('CPR6M', 'CPR6M', fmtPct1),
  numCol('CPR12M', 'CPR12M', fmtPct1),
  numCol('CDR1M', 'CDR1M', fmtPct1),
  numCol('CDR3M', 'CDR3M', fmtPct1),
  numCol('CDR6M', 'CDR6M', fmtPct1),
  numCol('CDR12M', 'CDR12M', fmtPct1),
  numCol('SEV1M', 'SEV1M', fmtPct1),
  numCol('SEV3M', 'SEV3M', fmtPct1),
  numCol('SEV6M', 'SEV6M', fmtPct1),
  numCol('SEV12M', 'SEV12M', fmtPct1),
  numCol('DQ30P', 'DQ30P', fmtPct1),
  numCol('DQ60P', 'DQ60P', fmtPct1),
  numCol('LTV', 'LTV', fmtPct1),
  numCol('OCCUPANCY', 'OCCUPANCY', fmtPct1),
  numCol('WAC', 'WAC', fmtPct1),
  numCol('SPECSERV', 'SPECSERV', fmtPct1),
  numCol('NOIDY', 'NOIDY', fmtPct1),
  numCol('UTILIZATION', 'UTILIZATION', fmtPct1),
  numCol('MVOC', 'MVOC', fmtPct1),
  numCol('SSSGT', 'SSSGT', fmtPct1),

  // CCC (value * 100 then 1 decimal + %)
  numCol('CCC', 'CCC', fmtCCCPct),

  // Leverage (1 decimal + "x")
  numCol('LEVERAGE', 'LEVERAGE', fmtLeverage),

  // Ratings (text)
  textCol('RTG_SP', 'RTG SP'),
  textCol('RTG_MOODY', 'RTG MOODY'),
  textCol('RTG_FITCH', 'RTG FITCH'),
  textCol('GROUP_W_RATING', 'GROUP W RATING'),
  textCol('RTG_KBRA', 'RTG KBRA'),
  textCol('RTG_DBRS', 'RTG DBRS'),
  textCol('RTG_BSCR', 'RTG BSCR'),
  textCol('RTG_EJ', 'RTG EJ'),

  // Dates
  textCol('SETTLE_DT', 'SETTLE DT', { minWidth: 100 }),
  { field: 'ASOFDT', headerName: 'ASOFDT', valueFormatter: fmtDate },
  { field: 'SETTLE_DATE', headerName: 'SETTLE DATE', valueFormatter: fmtDate },

  // Misc text
  textCol('CALLABLE', 'CALLABLE'),
  textCol('INTEXSTATUS', 'INTEXSTATUS'),
  textCol('LOADID', 'LOADID'),
  textCol('COLLAT_MANAGER', 'COLLAT MANAGER'),
  textCol('ISSUER_TRUST', 'ISSUER TRUST'),
  textCol('NOTES', 'NOTES', { minWidth: 200, flex: 2, wrapText: true, autoHeight: true }),
];

const SurveillanceHistory = ({ historyData = [] }) => (
  <HistoryGrid
    fetchHistory={() => Promise.resolve({ data: historyData })}
    fetchParams={[historyData]}
    fieldNames={FIELD_NAMES}
    columns={COLUMNS}
  />
);

export default SurveillanceHistory;
